import { Captions, ExternalLink, Film, FileText, Headphones } from "lucide-react";
import { useEffect, useState } from "react";
import type { LessonVideo } from "../content/types";

type LessonMediaProps = {
  title: string;
  transcript: string;
  narrationAudioSrc?: string;
  narrationLabel?: string;
  video?: LessonVideo;
};

export function LessonMedia({
  title,
  transcript,
  narrationAudioSrc,
  narrationLabel,
  video,
}: LessonMediaProps) {
  const hasNarratedMedia = Boolean(video || narrationAudioSrc);
  const [showTranscript, setShowTranscript] = useState(!hasNarratedMedia);
  const [transcriptContent, setTranscriptContent] = useState(video?.src ? "" : transcript);
  const [transcriptError, setTranscriptError] = useState(false);

  useEffect(() => {
    if (!showTranscript || !video?.transcriptSrc || transcriptContent || transcriptError) return;

    const controller = new AbortController();
    fetch(video.transcriptSrc, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Transcript request failed: ${response.status}`);
        return response.text();
      })
      .then(setTranscriptContent)
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") return;
        setTranscriptError(true);
      });

    return () => controller.abort();
  }, [showTranscript, transcriptContent, transcriptError, video]);

  return (
    <section className="lesson-media" aria-label={`Learning media for ${title}`}>
      {video?.src ? (
        <video className="lesson-video" controls preload="metadata" playsInline>
          <source src={video.src} type={video.mimeType ?? "video/mp4"} />
          <track
            default
            kind="captions"
            src={video.captionsSrc}
            srcLang="en"
            label="English"
          />
          Your browser does not support embedded video. Use the transcript below.
        </video>
      ) : video?.externalUrl ? (
        <div className="external-video" role="group" aria-label={`Open ${video.title}`}>
          <Film size={28} aria-hidden="true" />
          <div>
            <strong>Watch this video in NotebookLM</strong>
            <span>{video.description}</span>
          </div>
          <a className="secondary-button" href={video.externalUrl} target="_blank" rel="noreferrer">
            Open video <ExternalLink size={16} aria-hidden="true" />
          </a>
        </div>
      ) : narrationAudioSrc ? (
        <audio
          aria-label={`Generated narration for ${title}`}
          className="lesson-audio"
          controls
          preload="metadata"
          src={narrationAudioSrc}
        >
          Your browser does not support embedded audio. Use the transcript below.
        </audio>
      ) : video ? (
        <div className="media-unavailable" role="status">
          <Film size={28} aria-hidden="true" />
          <div><strong>Video source not attached</strong><span>The reviewed chapter copy is available below; no playable file was supplied.</span></div>
        </div>
      ) : null}

      <div className="media-controls">
        <span className="media-icon" aria-hidden="true">
          {video ? (
            <Film size={20} />
          ) : narrationAudioSrc ? (
            <Headphones size={20} />
          ) : (
            <FileText size={20} />
          )}
        </span>
        <div className="media-copy">
          <strong>{video?.title ?? (narrationAudioSrc ? (narrationLabel ?? "Lesson narration") : "Lesson transcript")}</strong>
          <span>
            {video
              ? `${video.durationLabel} · ${video.src ? "Narrated video · English captions available" : video.description}`
              : narrationAudioSrc
                ? "Generated voice · transcript available"
                : "Readable lesson summary"}
          </span>
        </div>
        {video?.src ? (
          <span className="caption-status">
            <Captions size={17} aria-hidden="true" /> CC
          </span>
        ) : null}
        {video?.externalUrl ? (
          <a className="text-button" href={video.externalUrl} target="_blank" rel="noreferrer">
            NotebookLM source <ExternalLink size={14} aria-hidden="true" />
          </a>
        ) : null}
        <button
          className="text-button"
          type="button"
          aria-expanded={showTranscript}
          aria-controls={`transcript-${title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
          onClick={() => setShowTranscript((current) => !current)}
        >
          {showTranscript ? "Hide transcript" : "Read transcript"}
        </button>
      </div>

      {showTranscript ? (
        <div
          className="media-transcript"
          id={`transcript-${title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
        >
          <p className="transcript-note">
            {video?.src
              ? (video.transcriptNote ?? "Reviewed transcript generated with ElevenLabs Scribe v2.")
              : narrationAudioSrc
                ? "Transcript of the ElevenLabs-generated lesson narration."
                : "Readable version of this lesson."}
          </p>
          {transcriptError ? (
            <p role="alert">The transcript could not be loaded. The caption track remains available.</p>
          ) : transcriptContent ? (
            transcriptContent.split(/\n\s*\n/).map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))
          ) : (
            <p role="status">Loading transcript…</p>
          )}
        </div>
      ) : null}
    </section>
  );
}
